import { create } from 'zustand';
import { UnitFilterKey } from '@/types';

const SIDEBAR_COLLAPSED_KEY = 'fishoil.sidebarCollapsed';

/** Per-viewer preference only — wrapped in try/catch since a private window
 *  or blocked site data can make localStorage throw on access. */
function readStoredSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredSidebarCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Best-effort — losing the preference on reload isn't worth surfacing an error for.
  }
}

interface UiState {
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;

  /** Desktop-only: collapses the persistent sidebar to an icon-only strip. Remembered across reloads. */
  sidebarCollapsed: boolean;
  toggleSidebarCollapsed: () => void;

  notifPanelOpen: boolean;
  setNotifPanelOpen: (open: boolean) => void;

  unitFilter: UnitFilterKey;
  setUnitFilter: (filter: UnitFilterKey) => void;

  gridVisible: boolean;
  toggleGridVisible: () => void;

  selectedUnitId: string | null;
  setSelectedUnitId: (id: string | null) => void;

  /** Hides the banner/sidebar/topbar so a page's own content (the 3D view) can fill the whole viewport. */
  appChromeHidden: boolean;
  toggleAppChromeHidden: () => void;
}

export const useUiStore = create<UiState>((set) => ({
  mobileSidebarOpen: false,
  setMobileSidebarOpen: (open) => set({ mobileSidebarOpen: open }),

  sidebarCollapsed: readStoredSidebarCollapsed(),
  toggleSidebarCollapsed: () =>
    set((s) => {
      const next = !s.sidebarCollapsed;
      writeStoredSidebarCollapsed(next);
      return { sidebarCollapsed: next };
    }),

  notifPanelOpen: false,
  setNotifPanelOpen: (open) => set({ notifPanelOpen: open }),

  unitFilter: 'all',
  setUnitFilter: (filter) => set({ unitFilter: filter }),

  gridVisible: true,
  toggleGridVisible: () => set((s) => ({ gridVisible: !s.gridVisible })),

  selectedUnitId: null,
  setSelectedUnitId: (id) => set({ selectedUnitId: id }),

  appChromeHidden: false,
  toggleAppChromeHidden: () => set((s) => ({ appChromeHidden: !s.appChromeHidden })),
}));
